import { useEffect, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import type { ManagerRole } from '../api/managerApi';
import {
  DataTable,
  DurationInput,
  LoadingState,
  inlineFieldSx,
  useErrorToast,
  type DataTableColumn,
} from '../components/common';
import { useEventContext } from '../contexts/EventContext';
import { eventService } from '../services/eventService';
import { categoryService, type Category, type Group } from '../services/categoryService';
import { loadMyManagerProfile } from '../services/managerService';

/** LocalDateTime 문자열에서 'HH:mm:ss' 추출 */
function toHhmmss(gunTime: string) {
  const matched = /T(\d{2}:\d{2}:\d{2})/.exec(gunTime);
  return matched ? matched[1] : '';
}

/** 대회날짜('YYYY-MM-DD') + 'HH:mm:ss' → LocalDateTime 문자열 */
function toGunTime(eventDate: string, hhmmss: string) {
  return `${eventDate}T${hhmmss}`;
}

/** 'HH:mm:ss' 완성 여부 검증 (00~23시, 00~59분, 00~59초) */
function isValidHhmmss(value: string) {
  return /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(value);
}

function getNextSelectedCategoryId(current: number | null, list: Category[]) {
  if (current != null && list.some((category) => category.id === current)) {
    return current;
  }
  return list[0]?.id ?? null;
}

function isDuplicateGroupName(groups: Group[], groupName: string, excludedGroupId?: number) {
  const normalizedGroupName = groupName.trim();

  return groups.some(
    (group) => group.groupId !== excludedGroupId && group.groupName.trim() === normalizedGroupName
  );
}

/** Enter=확정, Esc=취소 키 핸들러 */
function submitKeys(confirm: () => void, cancel: () => void) {
  return (e: ReactKeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirm();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };
}

const iconStroke = {
  width: 15,
  height: 15,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const EditIcon = () => (
  <svg {...iconStroke} aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const TrashIcon = () => (
  <svg {...iconStroke} aria-hidden="true">
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
  </svg>
);

const ClockIcon = () => (
  <svg {...iconStroke} width={13} height={13} aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const tabularNum = { fontVariantNumeric: 'tabular-nums' } as const;

/** 표 안 인라인 입력(이름·시간) 공용 필드 스타일 */
const cellFieldSx = { ...inlineFieldSx, fontSize: 12, height: 30 };

type CategoryTabsBarProps = Readonly<{
  categories: Category[];
  selectedCategoryId: number | null;
  onSelectCategory: (id: number) => void;
  action?: ReactNode;
}>;

function CategoryTabsBar({
  categories,
  selectedCategoryId,
  onSelectCategory,
  action,
}: CategoryTabsBarProps) {
  if (categories.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: '1px solid #e5e7eb',
        mb: 2,
      }}
    >
      <Tabs
        value={selectedCategoryId ?? false}
        onChange={(_, value) => onSelectCategory(value as number)}
        variant="standard"
        sx={{
          minHeight: 44,
          flex: '1 1 auto',
          '& .MuiTabs-flexContainer': { flexWrap: 'wrap' },
          '& .MuiTabs-indicator': { backgroundColor: '#2563eb' },
        }}
      >
        {categories.map((category) => (
          <Tab
            key={category.id}
            value={category.id}
            disableRipple
            sx={{
              textTransform: 'none',
              minHeight: 44,
              fontSize: 14,
              color: '#64748b',
              '&.Mui-selected': { color: '#1d4ed8' },
            }}
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                {category.categoryName}
                <Box component="span" sx={{ fontSize: 12, color: '#94a3b8', ...tabularNum }}>
                  {category.groups.length}
                </Box>
              </Box>
            }
          />
        ))}
      </Tabs>
      {action && <Box sx={{ flexShrink: 0, pb: 0.5 }}>{action}</Box>}
    </Box>
  );
}

type EmptyCategoryPanelProps = Readonly<{
  isAddingCategory: boolean;
  newCategoryName: string;
  onNewCategoryNameChange: (name: string) => void;
  onAddConfirm: () => void;
  onAddCancel: () => void;
  onStartAdd: () => void;
  canManageCategories: boolean;
}>;

function EmptyCategoryPanel({
  isAddingCategory,
  newCategoryName,
  onNewCategoryNameChange,
  onAddConfirm,
  onAddCancel,
  onStartAdd,
  canManageCategories,
}: EmptyCategoryPanelProps) {
  return (
    <Box
      sx={{
        minHeight: 300,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: 3,
        py: 7,
        border: '1px solid #e5e7eb',
        borderRadius: 2,
        backgroundColor: '#fbfcfe',
        textAlign: 'center',
      }}
    >
      {canManageCategories && isAddingCategory ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <InputBase
            autoFocus
            placeholder="종목 이름"
            value={newCategoryName}
            onChange={(e) => onNewCategoryNameChange(e.target.value)}
            onKeyDown={submitKeys(onAddConfirm, onAddCancel)}
            sx={{ ...inlineFieldSx, fontSize: 14, height: 36, width: 160, color: '#1f2937' }}
          />
          <IconButton size="small" color="primary" onClick={onAddConfirm}>
            ✓
          </IconButton>
          <IconButton size="small" onClick={onAddCancel}>
            ✕
          </IconButton>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: '#eff6ff',
              color: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1.5,
            }}
          >
            <svg
              width={26}
              height={26}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M4 22V4a1 1 0 0 1 1-1h11l-1.5 4L16 10H5" />
              <path d="M4 22h6" />
            </svg>
          </Box>
          <Box sx={{ fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
            등록된 종목이 없습니다
          </Box>
          <Box sx={{ fontSize: 13, color: '#8a94a6', mb: 2 }}>
            종목을 추가하고 출발 그룹(건타임)을 구성해 보세요.
          </Box>
          {canManageCategories && (
            <Button
              variant="contained"
              size="small"
              disableElevation
              sx={{ fontWeight: 700 }}
              onClick={onStartAdd}
            >
              + 첫 종목 추가
            </Button>
          )}
        </>
      )}
    </Box>
  );
}

type CategoryTabsActionProps = Readonly<{
  canManageCategories: boolean;
  isAddingCategory: boolean;
  newCategoryName: string;
  onNewCategoryNameChange: (name: string) => void;
  onAddConfirm: () => void;
  onAddCancel: () => void;
  onStartAdd: () => void;
}>;

function CategoryTabsAction({
  canManageCategories,
  isAddingCategory,
  newCategoryName,
  onNewCategoryNameChange,
  onAddConfirm,
  onAddCancel,
  onStartAdd,
}: CategoryTabsActionProps) {
  if (!canManageCategories) {
    return null;
  }

  if (!isAddingCategory) {
    return (
      <Button
        variant="contained"
        size="small"
        disableElevation
        sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
        onClick={onStartAdd}
      >
        + 종목 추가
      </Button>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <InputBase
        autoFocus
        placeholder="종목 이름"
        value={newCategoryName}
        onChange={(e) => onNewCategoryNameChange(e.target.value)}
        onKeyDown={submitKeys(onAddConfirm, onAddCancel)}
        sx={{ ...inlineFieldSx, fontSize: 13, height: 32, width: 160 }}
      />
      <IconButton size="small" color="primary" onClick={onAddConfirm}>
        ✓
      </IconButton>
      <IconButton size="small" onClick={onAddCancel}>
        ✕
      </IconButton>
    </Box>
  );
}

function CategoriesPage() {
  const { selectedEventId } = useEventContext();
  const [categories, setCategories] = useState<Category[]>([]);
  const [eventDate, setEventDate] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // 종목(레일) 추가·수정·삭제확인 상태
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategoryName, setEditingCategoryName] = useState<string | null>(null);
  const [isConfirmingCategoryDelete, setIsConfirmingCategoryDelete] = useState(false);
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);

  // 건타임 그룹(디테일) 추가·수정 상태
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupTime, setNewGroupTime] = useState('');
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [editingGroupTime, setEditingGroupTime] = useState('');
  const [managerRole, setManagerRole] = useState<ManagerRole | null>(null);
  const { showError, errorToast } = useErrorToast();
  const canManageCategories = managerRole === 'SUPER' || managerRole === 'MIDDLE';

  const reloadCategories = async (eventId: number) => {
    const list = await categoryService.list(eventId);
    setCategories(list);
    return list;
  };

  useEffect(() => {
    let ignore = false;

    loadMyManagerProfile()
      .then((profile) => {
        if (!ignore) {
          setManagerRole(profile.role);
        }
      })
      .catch(() => {
        if (!ignore) {
          setManagerRole(null);
        }
      });

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (selectedEventId == null) return;

    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        const [list, detail] = await Promise.all([
          reloadCategories(selectedEventId),
          eventService.detail(selectedEventId),
        ]);
        setSelectedCategoryId((current) => getNextSelectedCategoryId(current, list));
        setEventDate(detail.eventDate);
      } catch (error) {
        showError(error);
      } finally {
        setIsLoading(false);
      }
    };

    void loadInitialData();
  }, [selectedEventId, showError]);

  if (selectedEventId == null) {
    return (
      <section className="page-placeholder">
        <div className="page-content page-content-wide">
          <p className="page-section">Event</p>
          <h1>종목 관리</h1>
          <Box sx={{ mt: 3, color: '#8a94a6', fontSize: 14 }}>
            선택된 대회가 없습니다. 대회 목록에서 대회를 먼저 선택해 주세요.
          </Box>
        </div>
      </section>
    );
  }

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId) ?? null;
  const sortedGroups = selectedCategory
    ? [...selectedCategory.groups].sort((a, b) => a.gunTime.localeCompare(b.gunTime))
    : [];

  /** 종목 전환 시 다른 종목의 입력/확인 상태가 새지 않도록 초기화 */
  const selectCategory = (id: number) => {
    setSelectedCategoryId(id);
    setEditingCategoryName(null);
    setIsConfirmingCategoryDelete(false);
    setIsAddingGroup(false);
    setNewGroupName('');
    setNewGroupTime('');
    setEditingGroupId(null);
  };

  // ----- 종목 핸들러 -----
  const handleAddCategoryConfirm = async () => {
    if (!canManageCategories) return;
    if (!newCategoryName.trim()) return;
    try {
      const created = await categoryService.createCategory(selectedEventId, newCategoryName.trim());
      await reloadCategories(selectedEventId);
      setSelectedCategoryId(created.id);
      setNewCategoryName('');
      setIsAddingCategory(false);
    } catch (error) {
      showError(error);
    }
  };

  const cancelAddCategory = () => {
    setNewCategoryName('');
    setIsAddingCategory(false);
  };

  const handleRenameCategoryConfirm = async () => {
    if (!canManageCategories) return;
    const categoryName = editingCategoryName?.trim();
    if (categoryName == null || categoryName === '' || selectedCategory == null) {
      return;
    }
    try {
      await categoryService.updateCategory(
        selectedEventId,
        selectedCategory.id,
        categoryName
      );
      setCategories((prev) =>
        prev.map((c) =>
          c.id === selectedCategory.id ? { ...c, categoryName } : c
        )
      );
      setEditingCategoryName(null);
    } catch (error) {
      showError(error);
    }
  };

  const handleDeleteCategory = async () => {
    if (!canManageCategories) return;
    if (selectedCategory == null) return;
    setIsDeletingCategory(true);
    try {
      await categoryService.deleteCategory(selectedEventId, selectedCategory.id);
      const list = await reloadCategories(selectedEventId);
      setSelectedCategoryId(list[0]?.id ?? null);
      setIsConfirmingCategoryDelete(false);
    } catch (error) {
      showError(error);
    } finally {
      setIsDeletingCategory(false);
    }
  };

  // ----- 건타임 그룹 핸들러 -----
  const handleAddGroupConfirm = async () => {
    if (!canManageCategories) return;
    if (!newGroupName.trim() || !newGroupTime || selectedCategory == null || eventDate == null) {
      return;
    }
    if (!isValidHhmmss(newGroupTime)) {
      showError(new Error('건타임 형식이 올바르지 않습니다. 24시간 표기로 시·분·초를 모두 입력해 주세요. (예: 14:00:00, 00:00:00~23:59:59 사이)'));
      return;
    }
    if (isDuplicateGroupName(selectedCategory.groups, newGroupName)) {
      showError(new Error('같은 종목에 동일한 이름의 건타임 그룹이 이미 존재합니다.'));
      return;
    }
    try {
      await categoryService.createGroup(selectedEventId, selectedCategory.id, {
        groupName: newGroupName.trim(),
        gunTime: toGunTime(eventDate, newGroupTime),
      });
      await reloadCategories(selectedEventId);
      setNewGroupName('');
      setNewGroupTime('');
      setIsAddingGroup(false);
    } catch (error) {
      showError(error);
    }
  };

  const cancelAddGroup = () => {
    setNewGroupName('');
    setNewGroupTime('');
    setIsAddingGroup(false);
  };

  const handleEditGroupConfirm = async () => {
    if (!canManageCategories) return;
    if (
      !editingGroupName.trim() ||
      !editingGroupTime ||
      editingGroupId == null ||
      selectedCategory == null ||
      eventDate == null
    ) {
      return;
    }
    if (!isValidHhmmss(editingGroupTime)) {
      showError(new Error('건타임 형식이 올바르지 않습니다. 24시간 표기로 시·분·초를 모두 입력해 주세요. (예: 14:00:00, 00:00:00~23:59:59 사이)'));
      return;
    }
    if (isDuplicateGroupName(selectedCategory.groups, editingGroupName, editingGroupId)) {
      showError(new Error('같은 종목에 동일한 이름의 건타임 그룹이 이미 존재합니다.'));
      return;
    }
    try {
      await categoryService.updateGroup(selectedEventId, selectedCategory.id, editingGroupId, {
        groupName: editingGroupName.trim(),
        gunTime: toGunTime(eventDate, editingGroupTime),
      });
      await reloadCategories(selectedEventId);
      setEditingGroupId(null);
      setEditingGroupName('');
      setEditingGroupTime('');
    } catch (error) {
      showError(error);
    }
  };

  const handleDeleteGroup = async (group: Group) => {
    if (!canManageCategories) return;
    if (selectedCategory == null) return;
    try {
      await categoryService.deleteGroup(selectedEventId, selectedCategory.id, group.groupId);
      await reloadCategories(selectedEventId);
    } catch (error) {
      showError(error);
    }
  };

  const startEditGroup = (group: Group) => {
    if (!canManageCategories) return;
    setEditingGroupId(group.groupId);
    setEditingGroupName(group.groupName);
    setEditingGroupTime(toHhmmss(group.gunTime));
  };

  const groupColumns: DataTableColumn<Group>[] = [
    {
      key: 'groupName',
      header: '그룹 이름',
      render: (row) => {
        if (row.groupId !== editingGroupId) {
          return <span style={{ fontWeight: 600 }}>{row.groupName}</span>;
        }
        return (
          <InputBase
            autoFocus
            fullWidth
            value={editingGroupName}
            onChange={(e) => setEditingGroupName(e.target.value)}
            onKeyDown={submitKeys(handleEditGroupConfirm, () => setEditingGroupId(null))}
            sx={cellFieldSx}
          />
        );
      },
    },
    {
      key: 'gunTime',
      header: '건타임',
      width: 200,
      render: (row) => {
        if (row.groupId !== editingGroupId) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#475569' }}>
              <ClockIcon />
              <span style={tabularNum}>{toHhmmss(row.gunTime)}</span>
            </Box>
          );
        }
        return (
          <DurationInput
            withSeconds
            allowNegative={false}
            placeholder="14:00:00"
            value={editingGroupTime}
            onChange={setEditingGroupTime}
            onKeyDown={submitKeys(handleEditGroupConfirm, () => setEditingGroupId(null))}
            sx={cellFieldSx}
          />
        );
      },
    },
    ...(canManageCategories
      ? [
          {
            key: '_actions',
            header: '',
            width: 88,
            align: 'right' as const,
            render: (row: Group) => {
              if (row.groupId === editingGroupId) {
                return (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton size="small" color="primary" onClick={handleEditGroupConfirm}>
                      ✓
                    </IconButton>
                    <IconButton size="small" onClick={() => setEditingGroupId(null)}>
                      ✕
                    </IconButton>
                  </Box>
                );
              }
              return (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', color: '#94a3b8' }}>
                  <IconButton
                    size="small"
                    aria-label="그룹 수정"
                    sx={{ color: 'inherit', '&:hover': { color: '#2563eb' } }}
                    onClick={() => startEditGroup(row)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="그룹 삭제"
                    sx={{ color: 'inherit', '&:hover': { color: '#dc2626' } }}
                    onClick={() => handleDeleteGroup(row)}
                  >
                    <TrashIcon />
                  </IconButton>
                </Box>
              );
            },
          },
        ]
      : []),
  ];

  const groupAddRow = canManageCategories && isAddingGroup ? (
    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
      <TableCell>
        <InputBase
          autoFocus
          fullWidth
          placeholder="그룹 이름 (예: 엘리트)"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          onKeyDown={submitKeys(handleAddGroupConfirm, cancelAddGroup)}
          sx={cellFieldSx}
        />
      </TableCell>
      <TableCell>
        <DurationInput
          withSeconds
          allowNegative={false}
          placeholder="14:00:00"
          value={newGroupTime}
          onChange={setNewGroupTime}
          onKeyDown={submitKeys(handleAddGroupConfirm, cancelAddGroup)}
          sx={cellFieldSx}
        />
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton size="small" color="primary" onClick={handleAddGroupConfirm}>
            ✓
          </IconButton>
          <IconButton size="small" onClick={cancelAddGroup}>
            ✕
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  ) : null;

  return (
    <section className="page-placeholder">
      <div className="page-content page-content-wide">
        <p className="page-section">Event</p>
        <h1>종목 관리</h1>
        <Box className="page-description" sx={{ mt: 0.75 }}>
          대회 종목과 출발 그룹(건타임)을 설정합니다.
        </Box>

        <Box sx={{ mt: 3 }}>
          {isLoading ? (
            <LoadingState message="종목 정보를 불러오는 중입니다." />
          ) : (
            <>
              <CategoryTabsBar
                categories={categories}
                selectedCategoryId={selectedCategoryId}
                onSelectCategory={selectCategory}
                action={
                  <CategoryTabsAction
                    canManageCategories={canManageCategories}
                    isAddingCategory={isAddingCategory}
                    newCategoryName={newCategoryName}
                    onNewCategoryNameChange={setNewCategoryName}
                    onAddConfirm={handleAddCategoryConfirm}
                    onAddCancel={cancelAddCategory}
                    onStartAdd={() => setIsAddingCategory(true)}
                  />
                }
              />

          {/* 디테일 패널 */}
          <Box>
            {selectedCategory ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    {editingCategoryName == null ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span style={{ fontSize: 20, fontWeight: 800, color: '#111827' }}>
                          {selectedCategory.categoryName}
                        </span>
                        {canManageCategories && (
                          <>
                            <IconButton
                              size="small"
                              aria-label="종목 이름 수정"
                              sx={{ color: '#94a3b8', '&:hover': { color: '#2563eb' } }}
                              onClick={() => setEditingCategoryName(selectedCategory.categoryName)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="종목 삭제"
                              sx={{ color: '#cbd5e1', '&:hover': { color: '#dc2626' } }}
                              onClick={() => setIsConfirmingCategoryDelete(true)}
                            >
                              <TrashIcon />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <InputBase
                          autoFocus
                          value={editingCategoryName}
                          onChange={(e) => setEditingCategoryName(e.target.value)}
                          onKeyDown={submitKeys(handleRenameCategoryConfirm, () =>
                            setEditingCategoryName(null)
                          )}
                          sx={{ ...inlineFieldSx, fontSize: 20, fontWeight: 800, height: 40 }}
                        />
                        <IconButton size="small" color="primary" onClick={handleRenameCategoryConfirm}>
                          ✓
                        </IconButton>
                        <IconButton size="small" onClick={() => setEditingCategoryName(null)}>
                          ✕
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  {canManageCategories && (
                    <Button
                      variant="contained"
                      size="small"
                      disableElevation
                      sx={{ flexShrink: 0, fontWeight: 700 }}
                      onClick={() => setIsAddingGroup(true)}
                      disabled={isAddingGroup}
                    >
                      + 건타임 그룹 추가
                    </Button>
                  )}
                </Box>

                <DataTable
                  columns={groupColumns}
                  rows={sortedGroups}
                  rowKey="groupId"
                  footer={groupAddRow}
                  emptyMessage="등록된 건타임 그룹이 없습니다. ‘건타임 그룹 추가’로 출발 그룹을 만드세요."
                />
              </>
            ) : (
              <EmptyCategoryPanel
                isAddingCategory={isAddingCategory}
                newCategoryName={newCategoryName}
                onNewCategoryNameChange={setNewCategoryName}
                onAddConfirm={handleAddCategoryConfirm}
                onAddCancel={cancelAddCategory}
                onStartAdd={() => setIsAddingCategory(true)}
                canManageCategories={canManageCategories}
              />
            )}
          </Box>
            </>
          )}
        </Box>
      </div>
      <Dialog
        open={isConfirmingCategoryDelete}
        onClose={() => setIsConfirmingCategoryDelete(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800, color: '#b91c1c' }}>
          종목 삭제
        </DialogTitle>
        <DialogContent>
          <Box sx={{ color: '#334155', fontSize: 14, lineHeight: 1.7 }}>
            ‘{selectedCategory?.categoryName}’ 종목을 삭제할까요?
            <br />
            출발 그룹 {selectedCategory?.groups.length ?? 0}개가 모두 삭제됩니다.
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmingCategoryDelete(false)} disabled={isDeletingCategory}>
            취소
          </Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            onClick={handleDeleteCategory}
            disabled={isDeletingCategory}
          >
            {isDeletingCategory ? '삭제 중' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
      {errorToast}
    </section>
  );
}

export default CategoriesPage;
